/**
 * DOM utilities to use native browser capabilities and avoid dependencies.
 *
 * @module dom.dom-util
 * @version 1.0.0
 * @since Sept 26 2019
 *
 * @typedef {Object} ProductData
 * @property {string} colorCode - The product color code (E.g. 002)
 * @property {string} masterId - The product master ID (E.g. 003168)
 * @property {string} originalPrice - The product original price (E.g. 148.00)
 * @property {string} productColor - The product color name (E.g. Onyx)
 * @property {string} productId - The product SKU (E.g. 889069580469)
 * @property {string} productName - The product name (E.g. Keegan Lace Top)
 * @property {string} productPrice - The product sale price (E.g. 98.00)
 * @property {string} productSize - The product size (E.g. M)
 * @property {string} variationGroupId - The product variation group ID (E.g. 003168-onyx)
 * @property {string} productBadge - The product is of Early Access or not (E.g. Early Access)
 */

const { breakpoints, ANIM_TIME_STANDARD_1, ANIM_EASE_IN_OUT } = require('./constants');

/**
 * Native querySelector to find first element in the dom with given selector
 *
 * @param  {string | Object} selector Target selector element.
 * @param  {string | Object} parent  Parent element, if it not provided
 * fallback to document.
 * @return {Object} returns the target as a DOM node
 */
const queryFirst = (exports.queryFirst = (selector, parent = document) => {
    if (!parent) return null;
    return parent.querySelector(selector);
});
/**
 * loadScript to asynchronously load a script tag in DOM
 *
 * @param  {string} source Script tag src value
 * @param {boolean} isAsync Set script to async true or false
 */
exports.loadScript = (source, isAsync = false) => {
    return new Promise(resolve => {
        const script = document.createElement('script');
        script.src = source;
        script.async = isAsync;
        script.onload = () => {
            resolve();
        };
        document.body.append(script);
    });
};
/**
 * loadInput to load an input tag in DOM
 *
 * @param  {string} referrerID Referrer ID value
 * @param {Array} formElement Form element
 */
exports.loadInput = (referrerID, formElement) => {
    const input = document.createElement('input');
    input.setAttribute('type', 'hidden');
    input.setAttribute('name', 'referrerID');
    input.setAttribute('class', 'referrerID');
    input.setAttribute('value', referrerID);
    formElement.appendChild(input);
};
/**
 * Native querySelectorAll to find all the dom element with given selector.
 *
 * @param  {string | Object} selector Target selector element.
 * @param  {string | Object} parent Parent element, if it not provided
 * fallback to document.
 * @return {nodeList | Array} Returns the target as a DOM node.
 */
const queryAll = (exports.queryAll = (selector, parent = document) => {
    if (!parent) return null;
    return [].slice.call(parent.querySelectorAll(selector), 0);
});

/**
 * Normalize different parent types to a native DOM element.
 *
 * @param  {HTMLElement|Document|jQuery|Object|string} parent Target parent,
 *     can be a DOM element, document, jQuery object, Swiper instance or selector string.
 * @return {HTMLElement|Document} Returns a DOM element or document node
 *     to be used as a query scope. Falls back to document if invalid.
 */
exports.asElement = function asElement(parent) {
    if (!parent) return document;
    if (parent.nodeType === 1 || parent.nodeType === 9) return parent;           // DOM element or document
    if (parent.jquery) return parent[0];                                          // jQuery object
    if (parent.el && (parent.el.nodeType === 1 || parent.el.nodeType === 9)) return parent.el; // Swiper instance
    if (typeof parent === 'string') return document.querySelector(parent);        // selector string
    return document;
};

/**
 * Returns the value(s) for the specified CSS rule(s), or all computed styles if no rule is specified
 *
 * @param  {Element} element Target element
 * @param  {string} rule CSS rule name (E.g. padding-top)
 * @return {string | Array | Object} Returns a string value, array of values, or styles object
 */
const getStyles = (exports.getStyles = (element, ...rules) => {
    const styles = getComputedStyle(element);

    if (!rules.length) {
        return styles;
    } else if (rules.length === 1) {
        return styles[rules[0]];
    }
    return rules.map(rule => styles[rule]);
});

/**
 * Remove DOM element
 *
 * @param  {string | Object} el Target element.
 */
exports.remove = el => {
    if (!el) return;
    if (Array.isArray(el) || el instanceof NodeList) {
        for (let i = 0, n = el.length; i < n; i++) {
            const node = el[i];
            if (node) node.parentElement.removeChild(node);
        }
    } else {
        el.parentElement.removeChild(el);
    }
};

/**
 * Set attribute to single or multiple elements
 *
 * @param  {string | Object} el Target  element.
 * @param  {string} attr attribute that need to be added.
 * @param  {string | boolean} val value that need to be set for an attribute.
 */
exports.setAttribute = (el, attr, val) => {
    if (!el) return;
    if (Array.isArray(el) || el instanceof NodeList) {
        for (let i = 0, n = el.length; i < n; i++) {
            const node = el[i];
            if (node) node.setAttribute(attr, val);
        }
    } else {
        el.setAttribute(attr, val);
    }
};

/**
 * Remove attribute to single or multiple elements
 *
 * @param  {string | Object} el Target  element.
 * @param  {string} attr attribute that need to be removed.
 */
exports.removeAttribute = (el, attr) => {
    if (!el) return;
    if (Array.isArray(el) || el instanceof NodeList) {
        for (let i = 0, n = el.length; i < n; i++) {
            const node = el[i];
            if (node) node.removeAttribute(attr);
        }
    } else {
        el.removeAttribute(attr);
    }
};

/**
 * Adds class or classes to the classList of particular element.
 *
 * @param  {string | Object} el Target  element.
 * @param  {string | Array} val class name or class names as list of arguments.
 */
exports.addClass = (el, ...val) => {
    if (!el) return;
    if (Array.isArray(el) || el instanceof NodeList) {
        for (let i = 0, n = el.length; i < n; i++) {
            const node = el[i];
            if (node) node.classList.add(...val);
        }
    } else {
        el.classList.add(...val);
    }
};

/**
 * Removes class or classes from the classList of particular element.
 *
 * @param  {string | Object} el Target  element.
 * @param  {string | Array} val class name or class names as list of arguments.
 */
exports.removeClass = (el, ...val) => {
    if (!el) return;
    if (Array.isArray(el) || el instanceof NodeList) {
        for (let i = 0, n = el.length; i < n; i++) {
            const node = el[i];
            if (node) node.classList.remove(...val);
        }
    } else {
        el.classList.remove(...val);
    }
};

/**
 * Toggles class or classes to the classList of particular element.
 *
 * @param  {string | Object} el Target  element.
 * @param  {string | Array} val class name or class names as list of arguments.
 */
exports.toggleClass = (el, ...val) => {
    if (!el) return;
    if (Array.isArray(el) || el instanceof NodeList) {
        for (let i = 0, n = el.length; i < n; i++) {
            const node = el[i];
            if (node) node.classList.toggle(...val);
        }
    } else {
        el.classList.toggle(...val);
    }
};

/**
 * Looks for availability of class or classes in the classList of particular element.
 *
 * @param  {string | Object} el Target  element.
 * @param  {string | Array} val class name or class names as list of arguments.
 * @return {boolean} Returns availability of class in the target element.
 */
exports.hasClass = (el, ...classes) => {
    if (!el) return false;

    const { classList } = el;

    return !!classList && classes.every(className => classList.contains(className));
};

/**
 * Looks for availability of any one of multiple classes in the classList of particular element.
 *
 * @param  {string | Object} el Target  element.
 * @param  {string | Array} val class name or class names as list of arguments.
 * @return {boolean} Returns availability of class in the target element.
 */
exports.hasAnyClass = (el, ...classes) => {
    if (!el) return false;

    const { classList } = el;

    return !!classList && classes.some(className => classList.contains(className));
};

/**
 * Throttle util to prevent repeatative callbacks
 *
 * @param  {function} callback Callback function.
 * @param  {number} limit Timeout upon which throttle callback need to be called.
 * fallback to document.
 * @param  {function} waitCallback The callback to fire while the function is being throttled.
 * @return {function} Throttle closure function.
 */
exports.throttle = (callback, limit = 250, waitCallback = function () {}) => {
    let isThrottling = false;

    return (...args) => {
        if (isThrottling) return waitCallback(...args);

        const returnValue = callback.apply(this, args);
        isThrottling = true;

        setTimeout(() => {
            isThrottling = false;
        }, limit);

        return returnValue;
    };
};

/**
 * Throttle util to prevent repeatative callbacks
 *
 * @param {function} callback - Callback function.
 * @param {number} minimumDelay - Delay upon which debounce callback need to be called.
 * @param {object} [options] - Additional options to control debouncing behavior
 * @param {function} [options.condition] - A function that will cause debouncing to be ignored for one cycle if it returns true
 * @param {function} [options.callback] - A function that will run without restriction, each time the debounced method is called
 * @return {function} Debounce closure function.
 */
exports.debounce = (callback, minimumDelay = 250, options = {}) => {
    const { condition, callback: unrestrictedCallback } = options;
    let timer;

    return (...args) => {
        clearTimeout(timer);

        if (typeof unrestrictedCallback === 'function') unrestrictedCallback(...args);

        if (typeof condition === 'function' && condition(...args)) {
            callback(...args);
        } else {
            timer = setTimeout(() => {
                callback(...args);
            }, minimumDelay);
        }
    };
};

exports.toArray = (...args) => {
    const makeArray = target => {
        let newTarget;
        if (!Array.isArray(target)) {
            const array = [].slice.call(target);
            newTarget = array.length ? array : [target];
        }
        return newTarget;
    };
    return args.reduce((results, arg) => {
        return results.concat(makeArray(arg));
    }, []);
};

/**
 * Height util to get outer height of element including margins
 *
 * @param  {string | Object} el target element
 * @return  {number} height of element
 */
exports.outerHeight = el => {
    if (!el) return -1;
    const styles = getStyles(el);

    return el.offsetHeight + parseInt(styles.marginTop, 10) + parseInt(styles.marginBottom, 10);
};

/**
 * Determine if an element is in the viewport
 * (c) 2017 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param  {Node} el The element
 * @return {boolean} Returns true if element is in the viewport
 */
exports.isInViewportCheck = el => {
    const scroll = window.scrollY || window.pageYOffset;
    const boundsTop = el.getBoundingClientRect().top + scroll;

    const numberOfViewportToCheck = el.dataset.nextViewportLazyLoad ? 2 : 1;

    const viewport = {
        top: scroll,
        bottom: scroll + window.innerHeight * numberOfViewportToCheck
    };

    const bounds = {
        top: boundsTop,
        bottom: boundsTop + el.clientHeight
    };

    return (bounds.bottom >= viewport.top && bounds.bottom <= viewport.bottom) || (bounds.top <= viewport.bottom && bounds.top >= viewport.top);
};

/**
 * Determine if an element is in the viewport
 * (c) 2017 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param  {Node} el The element
 * @return {boolean} Returns true if element is in the viewport
 */
exports.isInViewport = el => {
    var rect = el.getBoundingClientRect();

    return rect.bottom > 0 && rect.right > 0 && rect.left < (window.innerWidth || document.documentElement.clientWidth) && rect.top < (window.innerHeight || document.documentElement.clientHeight);
};

/**
 * Animates scrolling the page to the specified Y coordinate
 * @param {number} y - The Y coordinate to scroll to
 * @param {number} duration - The duration of the animation in milliseconds
 * @param {function} callback - The callback to run when the animation is complete
 * @returns {JQuery} jquery object
 */
exports.scrollTo = (y, duration = 600, callback = function () {}) => $('html, body').scrollTo(y, duration, callback);

/**
 * Finds the last element sibling on the same line as the specified element
 * @param {Element} element - The element to reference when looking for siblings
 * @returns {Element} - returns last element in the row
 */
exports.getLastElementInRow = element => {
    if (!element.nextElementSibling) return element;

    const bottom = element.offsetTop + element.scrollHeight;
    let sibling;

    while ((sibling = element.nextElementSibling)) {
        if (sibling.offsetTop >= bottom) return sibling.previousElementSibling;
        element = sibling;
    }

    return element;
};

/**
 * Update url with query parameter to handle remember placement
 *
 * @param {DocumentFragment} fragment - A DocumentFragment object
 * @return {undefined}
 */
exports.updateUrlQueryParam = fragment => {
    const queryParamsElement = queryFirst('.append-params', fragment);
    const permaLink = queryFirst('.permalink', fragment);
    const url = new URL(window.location.href);
    const hash = url.hash; // Save the current hash

    let newUrl;

    if (permaLink) {
        newUrl = permaLink.value;
    } else if (queryParamsElement) {
        const baseUrl = window.location.href.split('?')[0];
        newUrl = baseUrl + '?' + queryParamsElement.value;
    }

    // Append the hash if it exists
    if (hash) {
        newUrl += hash;
    }

    // Update the history state if newUrl is calculated
    if (newUrl) {
        window.history.pushState({ path: newUrl }, '', newUrl);
    }
};

/**
 * This method returns character count
 * @param {HTMLElement} element - current element for which count has to be calculated
 * @return {number} character count
 */
exports.getCharacterCount = function (element) {
    if (!element) {
        return 0;
    }
    return element.value.length;
};

/**
 *  Waits for a specified element to exist.
 *  @param {string} selector - The selector to match
 *  @param {element} scope - The containing element to search within (default: document)
 *  @param {number} timeout - How long (in milliseconds) to wait before rejecting the promise
 *  @returns a Promise containing all matched elements
 */
exports.waitForElement = (selector, scope = document, timeout = 0) =>
    new Promise((resolve, reject) => {
        const elements = queryAll(selector, scope);

        if (elements.length) return resolve(elements);

        const observer = new MutationObserver((mutations, self) => {
            const elements = queryAll(selector, scope);

            if (elements.length) {
                self.disconnect();
                resolve(elements);
            }
        });

        observer.observe(scope, { childList: true, subtree: true });

        if (timeout) {
            setTimeout(() => {
                observer.disconnect();
                reject('Timed out');
            }, timeout);
        }
    });

/**
 *  Binds an event listener to a callback using native methods and selector matching.
 *  @param {element} element - The element to attach the listener to
 *  @param {string|array} eventTypes - The type of events to listen for
 *  @param {function} callback - The method to run when the event is fired
 *  @param {object} [options={}] - Additional options to configure the event binding
 *  @param {function} [options.eventCondition] - A function that returns a boolean indicating whether to proceed with the callback when the event is triggered
 *  @param {string} [options.bindEngine='native'] - The engine to use when binding the event (native or jQuery)
 *  @param {boolean} [options.once=false] - Whether to fire the event callback only once and then unbind
 */
exports.bind = (element, eventTypes, callback, options = {}) => {
    const { eventCondition, bindEngine = 'native', once = false } = options;

    if (!Array.isArray(eventTypes)) eventTypes = eventTypes.split(',').map(type => type.trim());

    const useJQuery = String(bindEngine).toLowerCase() === 'jquery';
    let { targetSelectors = [] } = options;

    if (typeof targetSelectors === 'string') {
        targetSelectors = [targetSelectors];
    }

    const handler = (e, extraData) => {
        let { target } = e;

        if (extraData instanceof Element || extraData === document) {
            target = extraData;
        } else if (extraData instanceof jQuery.Event) {
            target = extraData.target;
        } else {
            e.data = extraData;
        }

        let targetElement = target;

        if (Array.isArray(targetSelectors) && targetSelectors.length) {
            // If an array of selectors is provided, store a reference to the closest element that is matched
            targetElement = targetSelectors.reduce((result, selector) => {
                if (result) return result;
                const node = target.closest(selector);
                if (node) result = { node, selector };
                return result;
            }, false);
        }

        // We need a target element as context or we don't execute our callback
        if (!targetElement) return;

        const { node, selector } = targetElement;

        // Only execute the callback if the eventCondition allows
        if (typeof eventCondition === 'function' && !eventCondition(e, node || targetElement)) {
            return;
        }

        if (once) {
            if (useJQuery) {
                const $element = $(element);

                eventTypes.forEach(eventType => {
                    $element.off(eventType, handler);
                });
            } else {
                eventTypes.forEach(eventType => {
                    element.removeEventListener(eventType, handler, true);
                });
            }
        }

        if (node && selector) {
            callback(e, node, selector);
        } else {
            callback(e, targetElement);
        }
    };

    if (useJQuery) {
        const $element = $(element);

        eventTypes.forEach(eventType => {
            $element.on(eventType, handler);
        });
    } else {
        eventTypes.forEach(eventType => {
            element.addEventListener(eventType, handler, true);
        });
    }
};

/**
 * get parsed productInfo object from data attribute
 * @param {HTMLElement} container - current product-tile element
 * @return {Object|boolean} parsed object
 */
exports.getProductInfoFromDataAttr = function (container) {
    const productInfo = container?.dataset?.productInfo;
    if (!productInfo) return false;

    let parsedProductInfo;
    try {
        parsedProductInfo = JSON.parse(productInfo);
    } catch {
        return false;
    }

    return parsedProductInfo;
};

/**
 * get adjusted final sale price
 * @param {HTMLElement} container - container that has price
 * @returns {string} - string value price
 */
exports.getSalesPrice = container => {
    if (!container) return '';

    const priceEl = queryFirst('.price-section .sales .ssSalesPrice', container);
    return priceEl && ((priceEl && priceEl.value) || '').split('$').pop();
};

/**
 * Function to get product data
 * @param {HTMLElement} element - Product container element or srcElement
 * @returns {ProductData} - Product data
 */
exports.getProductData = element => {
    const { getNestedValue } = require('./util');
    const PREORDER_STATUS = 'PREORDER';

    element = element.closest('.color-attribute, .product-tile-swatch, .tile-img-link, .add-to-cart, .account-login-btn, .create-account-btn') || element;

    const container = element.closest('.product, .product-detail') || element.closest('.product-info') || element.closest('.cart-product-line-item') || element;
    const { dataset } = element;
    const { dataset: containerData } = container;
    const { monetatePid, viewType } = containerData;
    const isQuickview = !!container.closest('.product-quickview');

    let { productInfo, productInventory } = window;

    // Update the product data where possible
    let pid = containerData.wishlistId || containerData.pid || dataset.pid;
    let masterId = dataset.masterid || containerData.masterid || containerData.masterId;
    let productName = dataset.productName || containerData.productName;
    let productSize = dataset.productSize || containerData.productSize;
    let swatchName = dataset.swatchName || containerData.swatchName;
    let { attrValue: colorCode, productSet } = dataset;
    const { productBadge } = dataset;

    let variationGroupId;
    let originalPrice;
    let productPrice;
    let isPreorder;

    let productId =
        [dataset.productId, containerData.productId, pid].find(id => {
            if (id && ~id.indexOf('-')) {
                if (!variationGroupId) variationGroupId = id;

                return false;
            }

            return id;
        }) || '';

    // We need the color code, which is only available on the swatch
    if (!swatchName || !element.closest('.color-attribute, .product-tile-swatch')) {
        const colorEl = queryFirst('.color-attribute .swatch-circle.selected, .product-tile-swatch.selected', container) || queryFirst('.selected-swatch-name', container) || queryFirst('.tile-img-link', container);
        const swatchButton = colorEl && colorEl.closest('.color-attribute');

        swatchName = (colorEl && (colorEl.dataset.attrDisplayvalue || colorEl.dataset.swatchName || colorEl.textContent.trim())) || '';
        colorCode = (colorEl && colorEl.dataset.attrValue) || '';
        variationGroupId = variationGroupId || (swatchButton && swatchButton.dataset.productId) || '';
    }

    // Use the PID if the productId is missing or set to master
    if (!productId || (productId.length <= 6 && pid)) productId = pid;

    // We need the master ID to look up the product
    if (!masterId && monetatePid) {
        masterId = monetatePid.split('-')[0] || '';
    }

    if (!masterId) {
        if (pid && (~pid.indexOf('-') || pid.length <= 6)) {
            masterId = pid.split('-')[0];
        } else if (productId && ~productId.indexOf('-')) {
            masterId = productId.split('-')[0];
        } else {
            const tileEl = element.closest('.product-tile');
            masterId = ((tileEl && tileEl.dataset.monetatePid) || '').split('-')[0];
        }
    }

    // QuickView productInfo
    if (isQuickview) {
        productInfo = getNestedValue(window, 'quickviewProductInfo.productInfo');
        productInventory = getNestedValue(window, 'quickviewProductInfo.productInventory');
    }

    // Now we can check the productInfo
    if (productInfo && masterId && colorCode) {
        let productData;
        if (isQuickview) {
            productData = getNestedValue(productInfo, 'variants.{0}', colorCode);
        } else {
            productData = getNestedValue(productInfo, '{0}.variants.{1}', masterId, colorCode);
        }

        if (productData) {
            originalPrice = productData.standardPrice + '.00';
            productPrice = productData.price + '.00';
            variationGroupId = productData.groupId;
            isPreorder = productData.isPreorder;
        }
    }

    if (!productName) {
        const productNameEl = queryFirst('.product-name, .line-item-name', container);
        productName = (productNameEl && productNameEl.textContent.trim()) || '';
    }

    if (!productSize) {
        const productSizeEl = queryFirst('.size-btn.selected, .wishlist-item-attribute-size .wishlist-detail-value', container);
        productSize = (productSizeEl && (productSizeEl.dataset.valueId || productSizeEl.textContent.trim())) || '';
    }

    if (productSize && pid && viewType !== 'product-tile') {
        isPreorder = getNestedValue(productInventory?.[masterId], 'variants[{0}].availabilityStatus', pid) === PREORDER_STATUS;
    }

    if (!originalPrice) {
        const originalPriceEl = queryFirst('.price-section .ssPrice', container);
        originalPrice = ((originalPriceEl && originalPriceEl.value) || '').split('$').pop();
    }

    if (originalPrice && !~originalPrice.indexOf('.')) {
        originalPrice += '.00';
    }

    // get sales price
    if (!productPrice) productPrice = this.getSalesPrice(container) || '';

    // get last adjusted price (after all promotions) from data-product-info
    if (!productPrice) {
        const productInfoData = this.getProductInfoFromDataAttr(queryFirst('.js-product-tile', container));
        const variants = productInfoData && getNestedValue(productInfoData, '{0}.variants', masterId);
        if (variants) {
            const colorCode = Object.keys(variants).find(variant => variants[variant].groupId === pid);
            const formattedPrice = colorCode && variants[colorCode]?.formattedPrice;
            if (formattedPrice) productPrice = formattedPrice.split('$').pop();
        }
    }

    if (productPrice && !~productPrice.indexOf('.')) {
        productPrice += '.00';
    }

    if (!swatchName) {
        swatchName = dataset.swatchName || '';
    }

    // Sanity check
    if (masterId && masterId.length > 6) masterId = '';
    if (productId && (~productId.indexOf('-') || productId.length <= 6)) productId = '';
    if (variationGroupId && !~variationGroupId.indexOf('-')) variationGroupId = '';
    if (productSet === 'null') productSet = ''; // not all PLP tiles have data-product-set

    return {
        colorCode,
        masterId,
        originalPrice,
        productColor: swatchName,
        productId,
        productName,
        productPrice,
        productSize,
        variationGroupId,
        isPreorder,
        productBadge,
        productSet
    };
};

/**
 * Check whether the device matches the specified breakpoint at a minimum (i.e. min-width)
 * @param {string} breakpoint - The breakpoint to match against (xs; sm; md; lg; xl)
 * @returns {boolean} - Whether the device matches the breakpoint
 */
exports.matchesBreakpoint = breakpoint => breakpoint in breakpoints && window.matchMedia(`(min-width: ${breakpoints[breakpoint]}px)`).matches;

exports.getCSRFToken = (scope = document.body) => {
    const token = scope.querySelector('.csrf-token');
    return (token && token.value) || '';
};

/**
 * Renders a string into a template and returns the DOM Fragment.
 * @param {string} html - the html content.
 * @returns {DocumentFragment} - The input string as a document fragment
 */
exports.renderFragment = html => {
    const template = document.createElement('template');
    template.innerHTML = html;
    return template.content;
};

/**
 * Gets all accessible cookies and creates a plain key/value object
 * @returns {Object} Plain object containing cookie names and values
 */
exports.getCookieMap = () =>
    document.cookie.split(';').reduce((result, keyValue) => {
        const [key, value] = keyValue.trim().split('=');
        result[key] = decodeURIComponent(value);
        return result;
    }, {});

/**
 * Sets a cookie
 * @param {string} key - the name of the cookie
 * @param {string} value - the value of the cookie
 * @param {number} [minutes] - how long until the cookie expires in minutes
 */
exports.setCookie = (key, value, minutes) => {
    let date;
    if (minutes) {
        date = new Date();
        date.setTime(date.getTime() + minutes * 60 * 1000);
    }

    document.cookie = `${key}=${value}${date ? `; expires=${date.toGMTString()}` : ''}; path=/`;
};

/**
 * Validating the date as per month
 */
exports.validateDate = () => {
    const monthSelector = document.getElementById('birthdaymonth');
    const dateSelector = document.getElementById('birthdaydate');

    if (monthSelector && dateSelector) {
        const { message } = dateSelector.dataset;
        /**
         * Validating date
         */
        function validateDateOption() {
            const m = monthSelector.value;
            const d = dateSelector.value;
            const mlength = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m];
            dateSelector.length = 0;
            let isSelectModified = true;
            const labelOption = new Option();
            labelOption.text = message;
            labelOption.value = '';
            dateSelector.add(labelOption);
            for (let i = 1; i <= mlength; i++) {
                const option = new Option();
                option.value = i;
                option.text = i;
                if (i.toString() === d) {
                    option.selected = true;
                    isSelectModified = false;
                }
                dateSelector.add(option);
            }
            if (isSelectModified) {
                const changeEvent = new Event('change');
                dateSelector.dispatchEvent(changeEvent);
            }
        }
        monthSelector.addEventListener('change', validateDateOption);
    }
};

/**
 * Returns whether an element's content is visible to the user
 * Note that this differs from existing implementations like JQuery :visible -- this will return false
 * for elements which are visibility: hidden or which are overflow hidden with 0 height.
 * @param {HTMLElement} element - The element whose visibility to check
 * @returns {boolean} true if the element is visible, false otherwise.
 */
exports.isVisible = function (element) {
    if (!(element instanceof HTMLElement)) return false;

    // Check if the element is hidden using more performant methods first
    // We can't check for d-none here because we use conventions like "d-none d-md-block" on the site.
    if (exports.hasClass(element, 'invisible') || element.offsetParent === null) return false;

    const style = window.getComputedStyle(element);
    return !(style.display === 'none' || style.visibility === 'hidden' || (style.overflowY === 'hidden' && (style.height === '0px' || style.maxHeight === '0px')));
};

/**
 * Converts a basic object to FormData
 * @param {Object} obj - the object to convert to FormData
 * @param {boolean} [includeUndefined=false] - include undefined values in the result
 * @returns {FormData} FormData representation of the object
 */
exports.toFormData = (obj, includeUndefined = false) =>
    Object.keys(obj).reduce((formData, key) => {
        const value = obj[key];
        if (typeof value !== 'undefined' || includeUndefined) formData.append(key, value);
        return formData;
    }, new FormData());

/**
 * Slides an element up to hide it
 * @param {HTMLElement} elem - the element to animate
 * @param {number} duration - the duration of the animation in milliseconds
 * @param {string} easing - the easing function to use for the animation
 * @returns {Promise} - a promise which will resolve when the animation duration has elapsed
 */
exports.slideUp = (elem, duration = ANIM_TIME_STANDARD_1, easing = ANIM_EASE_IN_OUT) => {
    if (elem) {
        const { addClass } = this;
        const staticHeight = getComputedStyle(elem).getPropertyValue('height');
        elem.style.setProperty('--anim-static-height', staticHeight);
        elem.style.setProperty('--slide-up-duration', `${duration}ms`);
        elem.style.setProperty('--slide-up-ease', easing);
        addClass(elem, 'animate');

        // small delay is needed to process the new height added by the animate class before animating
        setTimeout(() => {
            addClass(elem, 'animate-slide-up');
        }, 10);
    }

    return new Promise(resolve => {
        setTimeout(() => {
            resolve();
        }, duration);
    });
};

/**
 * determines if it is recommendation ptoduct from Interaction Studio in mini-cart
 * @param {HTMLElement} srcElement - 'add to cart' button element
 * @returns {HTMLElement|undefined} - IS product container
 */
exports.getISProductContainer = srcElement => {
    const ISProductContainer = srcElement && srcElement.closest('.mini-cart-container #evg-minicart-recs');
    return ISProductContainer && ISProductContainer.contains(srcElement) && ISProductContainer;
};

/**
 * get product data from Interaction Studio container
 * @param {HTMLElement} container - IS product container
 * @returns {Object} - IS product data
 */
exports.getISProductData = container => {
    const { dataset } = queryFirst('.tile-img-link', container);
    const { evgItemId, productName, productSize, masterid } = dataset;

    return {
        productName: productName,
        productId: evgItemId,
        productSize,
        masterId: masterid,
        productColor: queryFirst('.evg-product-color', container)?.getElementsByTagName('div')[1]?.textContent,
        productPrice: this.getSalesPrice(container)
    };
};

/**
 * set adjusted sale price to tealiumPrices array
 * @param {Object} vgIDs - variation groups IDs array
 * @param {Object} tealiumPrices - tealium prices array
 * @returns {Object|boolean} - array with adjusted prices
 */
exports.setAdjustedSalesPrice = (vgIDs, tealiumPrices) => {
    if (!vgIDs || !tealiumPrices || vgIDs.length !== tealiumPrices.length) return false;

    const searchResultsContainer = queryFirst('.search-results.container');
    if (!searchResultsContainer) return false;

    for (let i = 0, len = vgIDs.length; i < len; i++) {
        const container = searchResultsContainer.querySelector('.js-product-tile-container:has([data-product-id="' + vgIDs[i] + '"])');
        const adjustedPrice = container && this.getSalesPrice(container);
        if (adjustedPrice && adjustedPrice !== tealiumPrices[i]) tealiumPrices[i] = adjustedPrice;
    }

    return tealiumPrices;
};

exports.getStyles = getStyles;
exports.queryAll = queryAll;
